/**
 * Tone — derive lightness-stop variants from any brand hex.
 *
 * Tailwind palettes (slate-50, blue-100, emerald-600, …) work by varying
 * lightness while keeping hue + saturation roughly constant. Sleek
 * Interface needs the same ramp for tenant brand colors so a customer
 * whose primaryColor is #D97706 (amber) gets sensible 50/100/600/900
 * tints without us shipping a fixed palette per tenant.
 *
 * Pure / synchronous — safe to inline into render bodies and DOCX
 * shading.fill arguments. No memoization required for typical site
 * report sizes (~50 calls per render).
 */

export type ToneStop = 50 | 100 | 200 | 600 | 700 | 800 | 900;

/**
 * Approximate Tailwind-style ramp lightness targets. Tailwind palettes
 * are perceptual rather than algorithmic, so these targets are tuned
 * to feel close to slate-50/-100/-600/-900 on a neutral-saturation input.
 */
const STOP_LIGHTNESS: Record<ToneStop, number> = {
  50:  97,
  100: 93,
  200: 86,
  600: 38,
  700: 31,
  800: 24,
  900: 16,
};

/** Parse a #rrggbb (or #rgb) string into [r,g,b] 0-255. Throws on invalid input. */
function parseHex(hex: string): [number, number, number] {
  const m = hex.trim().replace(/^#/, '');
  const expanded = m.length === 3 ? m.split('').map(c => c + c).join('') : m;
  if (!/^[0-9a-f]{6}$/i.test(expanded)) {
    throw new Error(`tone: invalid hex "${hex}"`);
  }
  return [
    parseInt(expanded.slice(0, 2), 16),
    parseInt(expanded.slice(2, 4), 16),
    parseInt(expanded.slice(4, 6), 16),
  ];
}

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  const rn = r / 255, gn = g / 255, bn = b / 255;
  const max = Math.max(rn, gn, bn), min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l * 100];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  switch (max) {
    case rn: h = ((gn - bn) / d + (gn < bn ? 6 : 0)); break;
    case gn: h = ((bn - rn) / d + 2); break;
    default: h = ((rn - gn) / d + 4); break;
  }
  return [h * 60, s * 100, l * 100];
}

function hslToHex(h: number, s: number, l: number): string {
  const sn = s / 100, ln = l / 100;
  const c = (1 - Math.abs(2 * ln - 1)) * sn;
  const hh = (((h % 360) + 360) % 360) / 60;
  const x = c * (1 - Math.abs((hh % 2) - 1));
  let r1 = 0, g1 = 0, b1 = 0;
  if (hh < 1) [r1, g1, b1] = [c, x, 0];
  else if (hh < 2) [r1, g1, b1] = [x, c, 0];
  else if (hh < 3) [r1, g1, b1] = [0, c, x];
  else if (hh < 4) [r1, g1, b1] = [0, x, c];
  else if (hh < 5) [r1, g1, b1] = [x, 0, c];
  else [r1, g1, b1] = [c, 0, x];
  const m = ln - c / 2;
  const to255 = (v: number) => Math.round((v + m) * 255).toString(16).padStart(2, '0');
  return `#${to255(r1)}${to255(g1)}${to255(b1)}`;
}

/**
 * Derive a Tailwind-stop equivalent hex from any input brand color.
 *
 * tone('#06b6d4', 50)  → very light cyan, suitable for bg-blue-50
 * tone('#06b6d4', 100) → light cyan,      suitable for bg-blue-100
 * tone('#06b6d4', 600) → mid cyan,        suitable for text-blue-600
 * tone('#06b6d4', 900) → dark cyan,       suitable for text-blue-900
 *
 * For 50/100/200, saturation is dampened to ~30%/40%/55% so the tint
 * reads as a subtle wash rather than a fluorescent block. For
 * 600/700/800/900 the original saturation is preserved.
 */
export function tone(hex: string, stop: ToneStop): string {
  const [r, g, b] = parseHex(hex);
  const [h, s] = rgbToHsl(r, g, b);
  const targetL = STOP_LIGHTNESS[stop];
  const dampS =
    stop === 50  ? Math.min(s, 30) :
    stop === 100 ? Math.min(s, 40) :
    stop === 200 ? Math.min(s, 55) :
    s;
  return hslToHex(h, dampS, targetL);
}

/** Convenience: a full ramp object for one input. */
export function toneRamp(hex: string): Record<ToneStop, string> {
  const stops: ToneStop[] = [50, 100, 200, 600, 700, 800, 900];
  const out: Partial<Record<ToneStop, string>> = {};
  for (const s of stops) out[s] = tone(hex, s);
  return out as Record<ToneStop, string>;
}
